import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Zap, FlaskConical, Clock, Video } from 'lucide-react';
import { BookCallModal } from './BookCallModal';

// Official Google Meet icon (compact)
const GoogleMeetIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 87.5 72" xmlns="http://www.w3.org/2000/svg">
    <path fill="#00832d" d="M49.5 36l8.53 9.75 11.47 7.33 2-17.02-2-16.64-11.69 6.44z"/>
    <path fill="#0066da" d="M0 51.5V66c0 3.315 2.685 6 6 6h14.5l3-10.96-3-9.54-9.95-3z"/>
    <path fill="#e94235" d="M20.5 0L0 20.5l10.55 3 9.95-3 2.95-9.41z"/>
    <path fill="#2684fc" d="M20.5 20.5H0v31h20.5z"/>
    <path fill="#00ac47" d="M82.6 8.68L69.5 19.42v33.66l13.16 10.79c1.97 1.54 4.85.135 4.85-2.37V11c0-2.535-2.945-3.925-4.91-2.32zM49.5 36v15.5h-29V72h43c3.315 0 6-2.685 6-6V53.08z"/>
    <path fill="#ffba00" d="M63.5 0h-43v20.5h29V36l20-16.57V6c0-3.315-2.685-6-6-6z"/>
  </svg>
);

export const Navbar = ({ onBookCall }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showBookCallModal, setShowBookCallModal] = useState(false);
  const location = useLocation();
  
  const handleBookCall = () => {
    if (onBookCall) {
      onBookCall();
    } else {
      setShowBookCallModal(true);
    }
    setIsOpen(false);
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    // EA Bot hidden - can be re-enabled later
    // { name: 'EA Bot', path: '/ea', icon: TrendingUp },
    // Inside Bar Algo hidden - can be re-enabled later
    // { name: 'Inside Bar Algo', path: '/inside-bar', icon: BarChart3 },
    { name: 'ORB Algo', path: '/orb-algo', icon: Clock },
    { name: 'Strategy Lab', path: '/strategy-lab', icon: FlaskConical },
  ];

  const isActive = (path) => location.pathname === path || (path !== '/' && location.pathname.startsWith(path));

  const handleSubscribe = () => {
    // Navigate to ORB Algo pricing section
    if (location.pathname === '/orb-algo') {
      // Already on ORB page, just scroll to pricing
      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Navigate to ORB page with pricing hash
      window.location.href = '/orb-algo#pricing';
    }
    setIsOpen(false); // Close mobile menu if open
  };

  return (
    <>
    <motion.nav
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="fixed top-0 left-0 right-0 z-50 px-4 py-3"
    >
      <div className="max-w-6xl mx-auto">
        <div className="glass-strong rounded-2xl px-5 py-3 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <img 
              src="/logo.png" 
              alt="Monk Algo" 
              className="w-9 h-9 rounded-lg object-cover"
            />
            <span className="font-heading font-semibold text-lg text-white">Monk Algo</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                className={`relative font-body text-sm font-medium transition-colors duration-200 ${
                  isActive(link.path) ? 'text-white' : 'text-white/50 hover:text-white'
                }`}
              >
                <span className="flex items-center gap-1.5">
                  {link.icon && <link.icon size={14} />}
                  {link.name}
                </span>
                {isActive(link.path) && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#007AFF]"
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={handleBookCall}
              data-testid="nav-book-call-btn"
              className="btn-purple text-sm flex items-center gap-2"
            >
              <GoogleMeetIcon className="w-4 h-4" />
              Book a Call
            </button>
            <button
              onClick={handleSubscribe}
              data-testid="nav-cta-button"
              className="px-4 py-2 rounded-lg border border-white/20 text-white/80 text-sm font-medium hover:bg-white/5 hover:border-white/30 transition-all flex items-center gap-2"
            >
              <Zap size={14} />
              View Plans
            </button>
          </div>

          {/* Mobile Book Call Button (always visible) */}
          <button
            onClick={handleBookCall}
            className="md:hidden flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#7c3aed] to-[#06b6d4] text-white text-xs font-medium"
            data-testid="mobile-header-book-call"
          >
            <GoogleMeetIcon className="w-3.5 h-3.5" />
            <span>Book a Call</span>
          </button>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-white p-2"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="md:hidden mt-3 glass-strong rounded-2xl p-5"
            >
              <div className="flex flex-col gap-3">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    className={`font-body text-base font-medium py-2 ${
                      isActive(link.path) ? 'text-[#007AFF]' : 'text-white/70'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {link.icon && <link.icon size={18} />}
                      {link.name}
                    </span>
                  </Link>
                ))}
                <button
                  onClick={handleBookCall}
                  className="btn-purple text-center mt-3 text-sm flex items-center justify-center gap-2"
                  data-testid="mobile-book-call-btn"
                >
                  <GoogleMeetIcon className="w-4 h-4" />
                  Book a Call
                </button>
                <button
                  onClick={() => {
                    setIsOpen(false);
                    handleSubscribe();
                  }}
                  className="px-4 py-2 rounded-lg border border-white/20 text-white/80 text-sm font-medium hover:bg-white/5 transition-all flex items-center justify-center gap-2"
                  data-testid="mobile-cta-button"
                >
                  <Zap size={14} />
                  View Plans
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
    
    {/* Book Call Modal */}
    <BookCallModal 
      isOpen={showBookCallModal} 
      onClose={() => setShowBookCallModal(false)} 
    />
    </>
  );
};
